import { useSubscription } from '@apollo/client';
import React, { createContext, FC, ReactNode, useRef, useState } from 'react';

import { ExtendedListing } from '../models/Listing';
import { ListingCreatedGQL, ListingUpdatedGQL, VehicleUpdatedSubscriptionGQL } from '../types/subscriptions/listings';

interface ListingsContextProps {
  selectedListing: ExtendedListing | undefined;
  setSelectedListing: React.Dispatch<React.SetStateAction<ExtendedListing | undefined>>;
  scrollableColumnRef: React.MutableRefObject<HTMLDivElement>;
  horizontalScrollBarRef: React.MutableRefObject<HTMLDivElement>;
  isProgrammaticScroll: React.MutableRefObject<boolean>;
  listScrollPosition: number;
  setListScrollPosition: (pos: number) => void;
  isNewListingsDataAvailable: boolean;
  setNewListingsDataAvailable: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ListingsProviderProps {
  children: ReactNode;
}

const ListingsContext = createContext<ListingsContextProps>(null);

export const ListingsProvider: FC<ListingsProviderProps> = ({ children }) => {
  const [selectedListing, setSelectedListing] = useState<ExtendedListing | undefined>(undefined);
  const [listScrollPosition, setListScrollPosition] = useState(0);
  const [isNewListingsDataAvailable, setNewListingsDataAvailable] = useState(false);

  // Refs
  const scrollableColumnRef = useRef<HTMLDivElement>(null); //Ref for the scrollable column of the cards
  const horizontalScrollBarRef = useRef<HTMLDivElement>(null); //Ref for the horizontal scrollbar below the cards
  const isProgrammaticScroll = useRef(false); //Ref used to detect scrolls made programatically and not by user interaction

  const handleListingSubscription = () => {
    setNewListingsDataAvailable(true);
  };

  useSubscription(ListingCreatedGQL, { onData: handleListingSubscription });
  useSubscription(ListingUpdatedGQL, { onData: handleListingSubscription });
  useSubscription(VehicleUpdatedSubscriptionGQL, { onData: handleListingSubscription });

  return (
    <ListingsContext.Provider
      value={{
        selectedListing,
        setSelectedListing,
        scrollableColumnRef,
        horizontalScrollBarRef,
        isProgrammaticScroll,
        listScrollPosition,
        setListScrollPosition,
        isNewListingsDataAvailable,
        setNewListingsDataAvailable,
      }}
    >
      {children}
    </ListingsContext.Provider>
  );
};

export const useListingsContext: () => ListingsContextProps = () => {
  return React.useContext(ListingsContext);
};
